import { ComponentType } from "react";

export enum MentionType {
  PEOPLE = "people",
  ASSETS = "assets",
  STAGE = "stage",
  ACTION = "action",
}

/**
 * Single source of truth for @-mention search tabs:
 * UI tab id, label, deal-room search API `type`, and serialized `MentionType`.
 */
export const AT_MENTION_SEARCH_TABS = [
  { value: "assets", label: "Assets", apiType: "asset", mentionType: MentionType.ASSETS },
  { value: "people", label: "People", apiType: "people", mentionType: MentionType.PEOPLE },
  { value: "actions", label: "Actions", apiType: "action", mentionType: MentionType.ACTION },
  { value: "stages", label: "Stages", apiType: "stage", mentionType: MentionType.STAGE },
] as const;

export type AtMentionSearchTab = (typeof AT_MENTION_SEARCH_TABS)[number]["value"];

export const DEFAULT_AT_MENTION_SEARCH_TAB: AtMentionSearchTab =
  AT_MENTION_SEARCH_TABS[0].value;

export function getAtMentionSearchApiType(tab: AtMentionSearchTab): string {
  const row = AT_MENTION_SEARCH_TABS.find((t) => t.value === tab);
  return row?.apiType ?? AT_MENTION_SEARCH_TABS[0].apiType;
}

export function getMentionTypeForAtMentionTab(tab: AtMentionSearchTab): MentionType {
  const row = AT_MENTION_SEARCH_TABS.find((t) => t.value === tab);
  return row?.mentionType ?? MentionType.ACTION;
}

export type Suggestion = {
  id: string;
  label: string;
  /** People tab: API `people.type` — serialized as `@[user:…]` or `@[contact:…]`. */
  serializedMentionType?: "user" | "contact";
  mentionType?: MentionType;
  [key: string]: any;
};

export type SuggestionPagination = {
  pageNumber: number;
  totalPagesCount: number;
  itemsPerPage: number;
  totalItemCount: string;
};

export type SuggestionSearchMeta = {
  tab?: AtMentionSearchTab;
  page?: number;
  limit?: number;
};

export type SuggestionSearchResult = {
  items: Suggestion[];
  pagination?: SuggestionPagination;
  hasMore?: boolean;
};

export type PopoverProps = {
  items: Suggestion[];
  activeIndex?: number;
  coords: { top: number; left: number; height: number } | null;
  onSelect: (item: Suggestion) => void;
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Close mention UI and reset parent state (e.g. outside click). */
  onDismiss?: () => void;
  /** Editor root; clicks inside stay open while editing the query. */
  getMentionAnchorRoot?: () => HTMLElement | null;
  /** Controlled @-mention search tab (assets / people / actions / stages). */
  mentionTab?: AtMentionSearchTab;
  onMentionTabChange?: (tab: AtMentionSearchTab) => void;
};

export type TriggerConfig = {
  char: string;
  getSuggestions: (
    query: string,
    meta?: SuggestionSearchMeta
  ) => Promise<Suggestion[] | SuggestionSearchResult>;
  PopoverComponent?: ComponentType<PopoverProps>;
  isLoading?: boolean;
  createMentionNode: (item: Suggestion) => Node;
};
